/**
 * Utility functions for the hierarchical table components
 */

/**
 * Formats a date for display with relative time and full date in tooltip
 */
export interface DateTimeDisplay {
    relativeTime: string; // For visible display (e.g., "3 days ago")
    fullDateTime: string; // For tooltip (e.g., "Apr 28, 2025, 10:15:23 AM")
}

/**
 * Formats a date to a readable string for display
 */
export const formatDateTime = (date: Date | undefined): DateTimeDisplay => {
    if (!date) {
        return {
            relativeTime: 'No entry',
            fullDateTime: ''
        };
    }

    const fullDateTime = formatDate(date);
    const minutesDuration = getMinutesBetweenDates(new Date(), date);
    const timeDisplay = formatTimeDuration(minutesDuration);

    return {
        relativeTime: `${timeDisplay.duration} ${timeDisplay.unit} ago`,
        fullDateTime: fullDateTime
    };
};

/**
 * Formats seconds into a human-readable hours, minutes, seconds format
 */
export const formatSecondsToHMS = (seconds: number | undefined): string => {
    if (seconds === undefined) return 'N/A';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (remainingSeconds > 0 || parts.length === 0) parts.push(`${remainingSeconds}s`);
    
    return parts.join(' ');
};

interface TimeDisplay {
    duration: number;
    unit: 'minutes' | 'hours' | 'days';
}

/**
 * Formats a time duration in minutes to a more readable form
 */
const formatTimeDuration = (minutesDuration: number): TimeDisplay => {
    // Constants for time conversions
    const MINUTES_IN_HOUR = 60;
    const MINUTES_IN_DAY = 1440; // 24 * 60

    if (minutesDuration >= MINUTES_IN_DAY) {
        // Convert to days if duration is >= 24 hours
        return {
            duration: Math.floor(minutesDuration / MINUTES_IN_DAY),
            unit: 'days',
        };
    } else if (minutesDuration >= MINUTES_IN_HOUR) {
        // Convert to hours if duration is >= 1 hour
        return {
            duration: Math.floor(minutesDuration / MINUTES_IN_HOUR),
            unit: 'hours',
        };
    } else {
        // Keep as minutes for durations < 1 hour
        return {
            duration: minutesDuration,
            unit: 'minutes',
        };
    }
};

/**
 * Gets the number of minutes between two dates
 */
const getMinutesBetweenDates = (startDate: Date, endDate: Date): number => {
    const diffInMs = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.floor(diffInMs / (1000 * 60));
};

/**
 * Formats a date to a readable string
 */
const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
    }).format(date);
};
